import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Header } from '../components/Header';
import { useT } from '../i18n';
import { PlatformConnections } from '../components/PlatformConnections';

interface AttentionItem {
  tenantId: string;
  name: string;
  plan: string;
  kind: string;
  daysLeft?: number;
  usagePct?: number;
}

interface CompanyRow {
  tenantId: string;
  name: string;
  plan: string;
  planName: string;
  status: string;
  users: number;
  contacts: number;
  connections: number;
  connected: number;
  sentThisMonth: number;
  messagesThisMonth: number;
  monthlyLimit: number;
  usagePct: number;
  sentToday: number;
  sentInPeriod: number;
  lastSentAt: string | null;
}

interface Overview {
  periodDays: number;
  schema?: { ok: boolean; missingTables: string[]; missingColumns: string[] };
  companies: { total: number; trial: number; active: number; suspended: number; expired: number; newThisMonth: number; newThisPeriod: number };
  plans: Array<{ id: string; name: string; companies: number; monthlyLimit: number }>;
  usage: { sentThisMonth: number; monthlyCapacity: number; unlimitedCompanies: number; capacityPct: number; sentToday: number; messagesThisMonth: number; sentInPeriod: number; failedInPeriod: number };
  connections: { total: number; connected: number; offline: number };
  campaigns: { running: number; scheduled: number; completedThisMonth: number };
  totals: { users: number; usersByRole: Record<string, number>; contacts: number };
  daily: Array<{ day: string; sent: number; failed: number; companies: number }>;
  attention: AttentionItem[];
  topCompanies: CompanyRow[];
  generatedAt: string;
}

const PERIODS = [14, 30, 90];

// Cada tipo de alerta tem sua cor: o vermelho é o que já parou de funcionar
const ATTENTION_TONE: Record<string, string> = {
  SUSPENDED: 'bg-red-50 border-red-200 text-red-800',
  SUBSCRIPTION_ENDED: 'bg-red-50 border-red-200 text-red-800',
  TRIAL_ENDED: 'bg-red-50 border-red-200 text-red-800',
  QUOTA_REACHED: 'bg-red-50 border-red-200 text-red-800',
  SUBSCRIPTION_ENDING: 'bg-amber-50 border-amber-200 text-amber-800',
  TRIAL_ENDING: 'bg-amber-50 border-amber-200 text-amber-800',
  QUOTA_NEAR: 'bg-amber-50 border-amber-200 text-amber-800',
  NO_CONNECTION: 'bg-slate-50 border-slate-200 text-slate-700',
};

const STATUS_STYLE: Record<string, { key: string; className: string }> = {
  ACTIVE: { key: 'sa.statusActive', className: 'bg-emerald-100 text-emerald-800' },
  TRIAL: { key: 'sa.statusTrial', className: 'bg-blue-100 text-blue-800' },
  EXPIRED: { key: 'sa.statusExpired', className: 'bg-amber-100 text-amber-800' },
  SUSPENDED: { key: 'sa.statusSuspended', className: 'bg-red-100 text-red-800' },
  INACTIVE: { key: 'sa.statusSuspended', className: 'bg-red-100 text-red-800' },
};

const formatNumber = (value: number) => value.toLocaleString('en-US');

function KpiCard({ label, value, sub, tone = 'text-gray-900' }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <p className="text-sm text-gray-500">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
      {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
    </div>
  );
}

export function SuperAdminDashboard() {
  const t = useT();
  const navigate = useNavigate();
  const [data, setData] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`/api/system/overview?days=${days}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error(String(response.status));
      setData(await response.json());
    } catch {
      toast.error(t('sa.errLoad'));
    } finally {
      setLoading(false);
    }
  }, [days, t]);

  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  const attentionLabel = (item: AttentionItem) => {
    if (item.kind === 'TRIAL_ENDING' || item.kind === 'SUBSCRIPTION_ENDING') {
      const key = item.daysLeft === 1 ? `sa.att${item.kind}_1` : `sa.att${item.kind}`;
      return t(key, { days: item.daysLeft ?? 0 });
    }
    if (item.kind === 'QUOTA_NEAR') return t('sa.attQUOTA_NEAR', { pct: item.usagePct ?? 0 });
    return t(`sa.att${item.kind}`);
  };

  const daily = data?.daily || [];
  const maxDaily = Math.max(1, ...daily.map(day => day.sent + day.failed));
  const hasActivity = daily.some(day => day.sent || day.failed);

  return (
    <>
      <Header
        title={t('sa.title')}
        subtitle={t('sa.subtitle')}
        actions={
          <div className="flex items-center gap-2">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {PERIODS.map(option => (
                <button
                  key={option}
                  onClick={() => setDays(option)}
                  className={`px-3 py-1 text-sm rounded-md ${days === option ? 'bg-white shadow-sm font-semibold' : 'text-gray-600'}`}
                >
                  {t(`sa.period${option}`)}
                </button>
              ))}
            </div>
            <button onClick={load} disabled={loading} className="btn-secondary text-sm disabled:opacity-50">
              ↻ {t('sa.refresh')}
            </button>
            <button onClick={() => navigate('/super-admin')} className="btn-primary text-sm">
              {t('sa.manage')}
            </button>
          </div>
        }
      />

      <div className="p-6 space-y-6">
        {loading && !data && <p className="text-center text-gray-400 py-16">{t('common.loading')}</p>}

        {data && (
          <>
            {data.schema && !data.schema.ok && (
              // Isto explica telas que respondem 500 sem motivo aparente
              <div className="rounded-xl border border-red-300 bg-red-50 p-4">
                <p className="font-semibold text-red-900">⚠️ {t('sa.schemaTitle')}</p>
                <p className="text-sm text-red-800 mt-1">{t('sa.schemaHelp')}</p>
                <p className="text-xs text-red-700 mt-2 font-mono break-all" dir="ltr">
                  {[...data.schema.missingTables, ...data.schema.missingColumns].join(' · ')}
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
              <KpiCard
                label={t('sa.kpiCompanies')}
                value={formatNumber(data.companies.total)}
                sub={t('sa.kpiCompaniesSub', { active: formatNumber(data.companies.active), trial: formatNumber(data.companies.trial) })}
              />
              <KpiCard
                label={t('sa.kpiNew')}
                value={formatNumber(data.companies.newThisMonth)}
                sub={t('sa.kpiNewSub', { count: formatNumber(data.companies.newThisPeriod), days: data.periodDays })}
                tone="text-emerald-700"
              />
              <KpiCard
                label={t('sa.kpiMessages')}
                value={formatNumber(data.usage.messagesThisMonth)}
                sub={t('sa.kpiMessagesSub', { count: formatNumber(data.usage.sentToday) })}
                tone="text-blue-700"
              />
              <KpiCard
                label={t('sa.kpiCapacity')}
                value={`${data.usage.capacityPct}%`}
                sub={t('sa.kpiCapacitySub', { used: formatNumber(data.usage.sentThisMonth), total: formatNumber(data.usage.monthlyCapacity) })}
              />
              <KpiCard
                label={t('sa.kpiConnections')}
                value={formatNumber(data.connections.total)}
                sub={t('sa.kpiConnectionsSub', { connected: formatNumber(data.connections.connected), offline: formatNumber(data.connections.offline) })}
                tone={data.connections.offline > 0 ? 'text-amber-700' : 'text-gray-900'}
              />
              <KpiCard
                label={t('sa.kpiCampaigns')}
                value={formatNumber(data.campaigns.running)}
                sub={t('sa.kpiCampaignsSub', { scheduled: formatNumber(data.campaigns.scheduled) })}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* O que precisa de decisão do dono da plataforma */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3">
                  {t('sa.attention')}
                  {data.attention.length > 0 && <span className="text-gray-400 font-normal"> ({data.attention.length})</span>}
                </h3>
                {data.attention.length === 0 ? (
                  <p className="text-sm text-gray-400 py-6 text-center">{t('sa.attentionEmpty')}</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {data.attention.map((item, index) => (
                      <button
                        key={`${item.tenantId}-${item.kind}-${index}`}
                        onClick={() => navigate('/super-admin')}
                        className={`w-full text-start border rounded-lg px-3 py-2 hover:brightness-95 ${ATTENTION_TONE[item.kind] || 'bg-gray-50 border-gray-200'}`}
                      >
                        <span className="block text-sm font-medium truncate" dir="auto">{item.name}</span>
                        <span className="block text-xs opacity-90">{attentionLabel(item)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Movimento diário da plataforma inteira */}
              <div className="bg-white rounded-xl border border-gray-200 p-4 lg:col-span-2">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-900">{t('sa.activity')}</h3>
                  <div className="flex gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-blue-500 inline-block" />{t('sa.legendSent')}</span>
                    <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 inline-block" />{t('sa.legendFailed')}</span>
                  </div>
                </div>

                {!hasActivity ? (
                  <p className="text-center text-sm text-gray-400 py-16">{t('sa.noActivity')}</p>
                ) : (
                  <div className="flex items-stretch gap-1 h-52" dir="ltr">
                    {daily.map((day, index) => {
                      const labelEvery = Math.ceil(daily.length / 12);
                      return (
                        <div key={day.day} className="flex-1 flex flex-col min-w-0 group relative">
                          <div className="flex-1 w-full flex items-end justify-center gap-[2px]">
                            <div className="w-1/2 bg-blue-500 rounded-t min-h-[2px]"
                                 style={{ height: `${Math.max((day.sent / maxDaily) * 100, day.sent ? 2 : 0)}%` }} />
                            <div className="w-1/3 bg-red-400 rounded-t"
                                 style={{ height: `${Math.max((day.failed / maxDaily) * 100, day.failed ? 2 : 0)}%` }} />
                          </div>
                          <span className="h-4 text-[9px] text-gray-400 truncate w-full text-center">
                            {index % labelEvery === 0 ? day.day.slice(5) : ''}
                          </span>
                          <div className="hidden group-hover:block absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[11px] rounded px-2 py-1 whitespace-nowrap z-10">
                            {day.day} · {formatNumber(day.sent)} {t('sa.legendSent')} · {formatNumber(day.failed)} {t('sa.legendFailed')}
                            {day.companies > 0 && ` · ${t('sa.newCompanies', { count: day.companies })}`}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Todos os números, de todas as empresas */}
            <PlatformConnections />

            {/* Planos: onde as empresas estão hoje */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">{t('sa.plans')}</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {data.plans.map(plan => (
                  <div key={plan.id} className={`rounded-lg border p-3 ${plan.companies > 0 ? 'border-gray-200' : 'border-dashed border-gray-200 opacity-60'}`}>
                    <p className="text-sm font-medium text-gray-900">{plan.name}</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{formatNumber(plan.companies)}</p>
                    <p className="text-[11px] text-gray-500 mt-1">
                      {plan.monthlyLimit === 0 ? t('sa.planUnlimited') : t('sa.planLimit', { count: formatNumber(plan.monthlyLimit) })}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Quem consome mais: é onde o custo e o risco estão */}
            <div className="bg-white rounded-xl border border-gray-200">
              <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-b">
                <h3 className="font-semibold text-gray-900">{t('sa.companies')}</h3>
                <p className="text-xs text-gray-500">
                  {t('sa.totals', { users: formatNumber(data.totals.users), contacts: formatNumber(data.totals.contacts) })}
                </p>
              </div>

              {data.topCompanies.length === 0 ? (
                <p className="p-10 text-center text-sm text-gray-400">{t('sa.noCompanies')}</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 text-start text-gray-500">
                      <tr>
                        <th className="px-4 py-2 text-start">{t('sa.colCompany')}</th>
                        <th className="px-4 py-2 text-start">{t('sa.colPlan')}</th>
                        <th className="px-4 py-2 text-start">{t('sa.colUsage')}</th>
                        <th className="px-4 py-2 text-start">{t('sa.colActivity', { days: data.periodDays })}</th>
                        <th className="px-4 py-2 text-start">{t('sa.colConnections')}</th>
                        <th className="px-4 py-2 text-start">{t('sa.colLastSend')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.topCompanies.map(company => {
                        const status = STATUS_STYLE[company.status] || STATUS_STYLE.ACTIVE;
                        const unlimited = company.monthlyLimit === 0;
                        return (
                          <tr key={company.tenantId} className="border-t hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <div className="font-medium text-gray-900 truncate max-w-[200px]" dir="auto">{company.name}</div>
                              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${status.className}`}>
                                {t(status.key)}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-700">{company.planName}</td>
                            <td className="px-4 py-2 min-w-[160px]">
                              {unlimited ? (
                                <span className="text-gray-500">{t('sa.unlimited')}</span>
                              ) : (
                                <>
                                  <div className="flex justify-between text-xs text-gray-600">
                                    <span>{formatNumber(company.messagesThisMonth)}</span>
                                    <span>{formatNumber(company.monthlyLimit)}</span>
                                  </div>
                                  <div className="h-2 bg-gray-100 rounded-full mt-1 overflow-hidden" dir="ltr">
                                    <div
                                      className={`h-full rounded-full ${company.usagePct >= 100 ? 'bg-red-500' : company.usagePct >= 80 ? 'bg-amber-400' : 'bg-emerald-500'}`}
                                      style={{ width: `${Math.min(company.usagePct, 100)}%` }}
                                    />
                                  </div>
                                  {company.sentThisMonth !== company.messagesThisMonth && (
                                    <p className="text-[10px] text-gray-400 mt-0.5">
                                      {t('sa.billedNote', { count: formatNumber(company.sentThisMonth) })}
                                    </p>
                                  )}
                                </>
                              )}
                            </td>
                            <td className="px-4 py-2 text-gray-700">{formatNumber(company.sentInPeriod)}</td>
                            <td className="px-4 py-2">
                              <span className={company.connected === 0 ? 'text-red-600' : 'text-gray-700'}>
                                {company.connected}/{company.connections}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-gray-500 whitespace-nowrap">
                              {company.lastSentAt ? (
                                <bdi dir="ltr">{new Date(company.lastSentAt).toLocaleDateString()}</bdi>
                              ) : t('sa.never')}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <p className="text-xs text-gray-400 text-center">
              {t('sa.updated', { time: new Date(data.generatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}
            </p>
          </>
        )}
      </div>
    </>
  );
}
